import { arcWedge, planBounds, shotEnd, type Sample } from '../../../lib/s3/plan';
import type { OverwatchPerchDef, OverwatchPlanDef } from '../../../lib/s3/types';

export type PlanFigure = { id: string; label: string; role?: string; at: Sample | null };
export type PlanShot = {
  perchId?: string;
  yawDeg?: number;
  impact?: { x: number; z: number } | null;
  hit?: boolean;
  head?: boolean;
  label: string;
};

const f = (v: number | null | undefined) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const roleCls: Record<string, string> = {
  hostile: 'fill-blam',
  escort: 'fill-benday',
  innocent: 'fill-caption',
};

/**
 * The scene plan, top down: zones (labelled), paths (with arrows at the end), exits, occluder boxes, and each perch with its yaw arc.
 * Optionally figures at one moment (coloured by role) and shots (a line from the perch: solid to the server's impact, dashed along the
 * yaw when the impact isn't known). Far (+z) is at the top.
 */
export function PlanView({
  plan,
  perches,
  figures,
  shots,
  maxRangeM = 300,
  label,
}: {
  plan: OverwatchPlanDef | null | undefined;
  perches: (OverwatchPerchDef | null)[];
  figures?: PlanFigure[];
  shots?: PlanShot[];
  maxRangeM?: number;
  label: string;
}) {
  const b = planBounds(plan, perches);
  const w = b.maxX - b.minX;
  const h = b.maxZ - b.minZ;
  const Y = (z: number) => -z; // SVG y grows down; far z goes up.
  const exits = new Set(plan?.exits ?? []);
  const arcLen = Math.min(60, Math.max(w, h) / 3);
  const s = Math.max(w, h) / 120; // a size unit that reads at any plan scale
  return (
    <svg
      viewBox={`${b.minX} ${-b.maxZ} ${w} ${h}`}
      className="max-h-[36rem] w-full border border-newsprint bg-white"
      role="img"
      aria-label={label}
    >
      {Object.entries(plan?.zones ?? {}).map(([id, z]) =>
        z ? (
          <g key={`z-${id}`}>
            <rect
              x={f(z.minX)}
              y={Y(f(z.maxZ))}
              width={f(z.maxX) - f(z.minX)}
              height={f(z.maxZ) - f(z.minZ)}
              className={
                exits.has(id) ? 'fill-benday/30 stroke-benday' : 'fill-newsprint/60 stroke-bruise'
              }
              strokeWidth={s * 0.3}
            />
            <text
              x={f(z.minX) + s}
              y={Y(f(z.maxZ)) + s * 3}
              fontSize={s * 2.5}
              className="fill-bruise"
            >
              {id}
            </text>
          </g>
        ) : null,
      )}
      {(plan?.occluders ?? []).map((o, i) =>
        o ? (
          <rect
            key={`o-${i}`}
            x={f(o.min?.[0])}
            y={Y(f(o.max?.[2]))}
            width={f(o.max?.[0]) - f(o.min?.[0])}
            height={f(o.max?.[2]) - f(o.min?.[2])}
            className="fill-ink/60"
          >
            <title>{o.id}</title>
          </rect>
        ) : null,
      )}
      {Object.entries(plan?.paths ?? {}).map(([id, p]) => {
        const pts = (p ?? []).filter((q) => !!q);
        if (pts.length < 2) return null;
        const last = pts[pts.length - 1]!;
        return (
          <g key={`p-${id}`}>
            <polyline
              points={pts.map((q) => `${f(q.x)},${Y(f(q.z))}`).join(' ')}
              fill="none"
              className={exits.has(id) ? 'stroke-benday' : 'stroke-bruise'}
              strokeDasharray={`${s} ${s * 0.6}`}
              strokeWidth={s * 0.4}
            />
            <circle cx={f(last.x)} cy={Y(f(last.z))} r={s * 0.8} className="fill-bruise" />
            <text x={f(last.x) + s} y={Y(f(last.z))} fontSize={s * 2.2} className="fill-bruise">
              {id}
            </text>
          </g>
        );
      })}
      {perches.map((p, i) => {
        if (!p) return null;
        const a = arcWedge(p, arcLen);
        return (
          <g key={`perch-${i}`}>
            <path
              d={`M ${a.eye.x} ${Y(a.eye.z)} L ${a.from.x} ${Y(a.from.z)} A ${arcLen} ${arcLen} 0 ${a.large ? 1 : 0} 0 ${a.to.x} ${Y(a.to.z)} Z`}
              className="fill-benday/10 stroke-benday"
              strokeWidth={s * 0.3}
            />
            <rect
              x={a.eye.x - s}
              y={Y(a.eye.z) - s}
              width={s * 2}
              height={s * 2}
              className="fill-ink"
            />
            <text x={a.eye.x + s * 1.5} y={Y(a.eye.z) + s} fontSize={s * 2.5} className="fill-ink">
              {p.id}
            </text>
          </g>
        );
      })}
      {(shots ?? []).map((sh, i) => {
        const perch = perches.find((p) => p?.id === sh.perchId) ?? perches[0] ?? undefined;
        const e = shotEnd(perch ?? undefined, sh, maxRangeM);
        return (
          <g key={`shot-${i}`} data-testid="shot">
            <line
              x1={e.from.x}
              y1={Y(e.from.z)}
              x2={e.to.x}
              y2={Y(e.to.z)}
              className={sh.hit ? 'stroke-blam' : 'stroke-ink'}
              strokeWidth={s * 0.3}
              strokeDasharray={e.known ? undefined : `${s * 1.5} ${s}`}
            />
            {e.known && (
              <circle
                cx={e.to.x}
                cy={Y(e.to.z)}
                r={s * (sh.head ? 1.4 : 1)}
                className={sh.hit ? 'fill-blam' : 'fill-none stroke-ink'}
                strokeWidth={s * 0.3}
              />
            )}
            <text x={e.to.x + s} y={Y(e.to.z)} fontSize={s * 2.2} className="fill-ink">
              {sh.label}
            </text>
          </g>
        );
      })}
      {(figures ?? []).map((fg) =>
        fg.at ? (
          <g key={`f-${fg.id}`} data-testid="figure">
            <circle
              cx={fg.at.x}
              cy={Y(fg.at.z)}
              r={s * 1.2}
              className={`${roleCls[fg.role ?? ''] ?? 'fill-bruise'} stroke-ink`}
              strokeWidth={s * 0.2}
            />
            <title>
              {fg.label}
              {fg.at.state ? ` · ${fg.at.state}` : ''}
            </title>
          </g>
        ) : null,
      )}
    </svg>
  );
}
